import React, { useState, useCallback, useRef } from 'react';
import { 
  Upload, 
  FileSpreadsheet, 
  FileText, 
  File,
  X, 
  Loader2, 
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Zap,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import ZipBatchUpload from './ZipBatchUpload';
import api, { uploadFiles as apiUploadFiles } from '../../utils/api';

interface UnifiedUploaderProps {
  onComplete: (data: {
    canonical_data: any;
    file_results: any[];
    stats: any;
    review_items: any[];
    tax1065Files?: any[];  // Tax form data for Step 4/5
  }) => void;
  onBack?: () => void;
  onCancel?: () => void;
  wizardData: any;  // Pass wizard context (company name, business type, event type, etc.)
  compact?: boolean;  // If true, renders without outer wrapper (for v2 sidebar layout)
  reportId?: string;  // If provided, results will be auto-saved to this report
}

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface FileResult {
  filename: string;
  file_type: string;
  success: boolean;
  transactions_extracted: number;
  periods_count: number;
  extraction_method: string;
  cache_hit: boolean;
  processing_time_seconds: number;
  errors: string[];
}

/**
 * UnifiedUploader - Smart file upload for Step 3 of the wizard
 * 
 * Uses the unified upload API to process multiple files (CSV, Excel, PDF)
 * in parallel and returns merged CanonicalData to the wizard flow.
 * 
 * Key features:
 * - Multi-file drag & drop
 * - Auto file type detection
 * - Parallel processing
 * - PDF caching for speed
 * - Year extraction from filename (e.g., PL_Statement_2008.csv)
 * - Passes wizard context (company name, business type) to backend
 */
export default function UnifiedUploader({ onComplete, onBack, onCancel, wizardData, compact = false, reportId }: UnifiedUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileResults, setFileResults] = useState<FileResult[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [showZipUpload, setShowZipUpload] = useState(false);

  // File type detection
  const getFileType = (file: File): 'pdf' | 'csv' | 'excel' | 'unknown' => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (ext === 'csv') return 'csv';
    if (['xlsx', 'xls', 'xlsm'].includes(ext || '')) return 'excel';
    return 'unknown';
  };

  // File icons
  const getFileIcon = (file: File) => {
    const type = getFileType(file);
    switch (type) {
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />;
      case 'csv':
        return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
      case 'excel':
        return <FileSpreadsheet className="w-5 h-5 text-emerald-500" />;
      default:
        return <File className="w-5 h-5 text-gray-400" />;
    }
  };

  // Add files
  const addFiles = useCallback((newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ['pdf', 'csv', 'xlsx', 'xls'].includes(ext || '');
    });

    if (validFiles.length !== newFiles.length) {
      const skipped = newFiles.length - validFiles.length;
      console.log(`Skipped ${skipped} unsupported files`);
    }

    const uploadedFiles: UploadedFile[] = validFiles.map(file => ({
      file,
      status: 'pending',
    }));

    setFiles(prev => [...prev, ...uploadedFiles]);
    setError(null);
    setUploadComplete(false);
  }, []);

  // Handle drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, [addFiles]);

  // Handle file select
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
  }, [addFiles]);

  // Remove file
  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  // State for streaming progress
  const [currentFile, setCurrentFile] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');

  // Upload files with real-time streaming progress
  const uploadFiles = async () => {
    if (files.length === 0) {
      setError('Please add at least one file');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    setStatusMessage('Preparing upload...');

    try {
      const formData = new FormData();

      // Add files
      files.forEach(({ file }) => {
        formData.append('files', file);
      });

      // Build metadata from wizard context
      const metadata = {
        company_name: wizardData.claimant_name || wizardData.client_name || '',
        business_type: wizardData.business_type || 'unknown',
        interruption_type: wizardData.event_type || null,
        interruption_start: wizardData.event_start_date || null,
        interruption_end: wizardData.event_end_date || null,
        enable_bert: false,
        use_cache: true,
        enable_textract: true,
        enable_claude: true,
        doc_type: 'auto', // Let the backend auto-detect document type
      };
      formData.append('metadata', JSON.stringify(metadata));
      
      // Also add doc_type as form field - 'auto' means auto-detect
      formData.append('doc_type', 'auto');
      
      // If we have a report_id, include it so results auto-save to the report
      if (reportId) {
        formData.append('report_id', reportId);
        console.log('📎 Upload linked to report:', reportId);
      }

      // Step 1: Start the upload job
      console.log('🚀 Starting upload job...');
      setStatusMessage('Starting upload...');
      setUploadProgress(5);
      
      // Use centralized API utility for file upload
      const startResult = await apiUploadFiles('/api/v1/upload/stream', formData, { showErrorToast: false });
      
      // Validate response
      if (!startResult || !startResult.job_id) {
        console.error('❌ Invalid start response:', startResult);
        throw new Error('Upload failed: Invalid response from server');
      }
      
      const { job_id } = startResult;
      console.log('📋 Job started:', job_id);
      setStatusMessage('Processing files...');
      setUploadProgress(10);

      // Step 2: Poll for progress (more reliable than SSE in this environment)
      let finalResult: any = null;
      let attempts = 0;
      const maxAttempts = 600; // 5 minutes max (500ms intervals)
      
      while (!finalResult && attempts < maxAttempts) {
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 500));
        
        try {
          // Use centralized API utility for status polling
          const status = await api.get(`/api/v1/upload/stream/${job_id}/status`).catch(() => null);
          
          if (status) {
            
            // Update progress UI
            if (status.percent !== undefined) {
              setUploadProgress(status.percent);
            }
            if (status.message) {
              setStatusMessage(status.message);
            }
            if (status.current_file) {
              setCurrentFile(status.current_file);
            }
            
            console.log(`📊 Progress: ${status.percent || 0}% - ${status.message || status.phase || 'processing'}`);
            
            // Check for completion
            if (status.status === 'completed') {
              // Fetch the final result using centralized API utility
              const resultResponse = await api.get(`/api/v1/upload/stream/${job_id}/result`).catch(() => null);
              if (resultResponse) {
                // The API returns {status: "completed", result: {...actual data...}}
                // Extract the actual result from the nested structure
                finalResult = resultResponse.result || resultResponse;
                console.log('✅ Upload complete!');
              }
              break;
            }
            
            if (status.status === 'failed') {
              throw new Error(status.message || 'Processing failed');
            }
          } else {
            // Job might not be registered yet or 404, wait a bit
            if (attempts < 10) {
              console.log('⏳ Waiting for job to start...');
              continue;
            }
            // After 5 seconds, fall back to regular upload
            console.log('⚠️ Job not found, falling back to regular upload');
            break;
          }
        } catch (pollError) {
          console.warn('Poll error:', pollError);
          // Continue polling
        }
      }

      // Fallback to regular endpoint if streaming didn't work
      if (!finalResult) {
        console.log('⚠️ Falling back to regular upload endpoint...');
        setStatusMessage('Processing (fallback)...');
        
        const fallbackFormData = new FormData();
        files.forEach(({ file }) => fallbackFormData.append('files', file));
        fallbackFormData.append('metadata', JSON.stringify(metadata));
        
        // Use centralized API utility for fallback upload
        finalResult = await apiUploadFiles('/api/v1/upload/claim-documents', fallbackFormData, { showErrorToast: false });
      }

      setUploadProgress(100);
      setStatusMessage('Complete!');
      setFileResults(finalResult.file_results || []);
      setStats(finalResult.stats || {});
      setUploadComplete(true);

      if (finalResult.success) {
        console.log('✅ Upload complete:', finalResult.stats);
        console.log('📋 tax1065Files in result:', finalResult.tax1065Files?.length || 0);
        
        // ✅ CRITICAL: Call onComplete with full result including tax1065Files
        onComplete({
          canonical_data: finalResult.canonical_data,
          file_results: finalResult.file_results || [],
          stats: finalResult.stats || {},
          review_items: finalResult.review_items || [],
          tax1065Files: finalResult.tax1065Files || [],
        });
      }

    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed');
      setStatusMessage('');
    } finally {
      setIsUploading(false);
      setCurrentFile('');
    }
  };

  // Proceed to next step with parsed data
  const handleProceed = async () => {
    if (!uploadComplete || !stats) return;

    // Fetch the full response again to get canonical_data
    const formData = new FormData();
    files.forEach(({ file }) => formData.append('files', file));
    
    const metadata = {
      company_name: wizardData.claimant_name || wizardData.client_name || '',
      business_type: wizardData.business_type || 'unknown',
      interruption_type: wizardData.event_type || null,
      interruption_start: wizardData.event_start_date || null,
      interruption_end: wizardData.event_end_date || null,
      enable_bert: false,
      use_cache: true,
    };
    formData.append('metadata', JSON.stringify(metadata));

    try {
      // Use centralized API utility for file upload
      const result = await apiUploadFiles('/api/v1/upload/claim-documents', formData, { showErrorToast: false });
      onComplete({
        canonical_data: result.canonical_data,
        file_results: result.file_results,
        stats: result.stats,
        review_items: result.review_items,
        tax1065Files: result.tax1065Files || [],
      });
    } catch (err) {
      console.error('Failed to get final data:', err);
      // Still proceed with what we have
      onComplete({
        canonical_data: null,
        file_results: fileResults,
        stats: stats,
        review_items: [],
        tax1065Files: [],
      });
    }
  };

  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={compact ? "h-full flex flex-col" : "min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6"}>
      <div className={compact ? "flex-1 flex flex-col p-6" : "max-w-4xl mx-auto space-y-6"}>
        {/* Header - only show in non-compact mode */}
        {!compact && (
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-3">Step 3: Upload Financial Documents</h1>
            <p className="text-lg text-gray-600">
              Drop all your files at once — P&L statements, bank statements, spreadsheets
            </p>
            {wizardData.claimant_name && (
              <p className="text-sm text-gray-500 mt-2">
                For: <span className="font-medium">{wizardData.claimant_name}</span>
                {wizardData.event_type && (
                  <span className="ml-2">| Event: <span className="font-medium capitalize">{wizardData.event_type.replace('_', ' ')}</span></span>
                )}
              </p>
            )}
          </div>
        )}

        {/* Drop Zone */}
        <div className={`${compact ? '' : 'mb-4'}`}>
          {/* Auto-detection notice */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700 flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span><strong>Smart Upload:</strong> Drop any documents — our AI will automatically detect the type (Bank Statements, P&L, Tax Returns, K-1s, etc.)</span>
            </p>
          </div>
        </div>

        {/* File Drop Zone */}
        <div
          className={`border-2 border-dashed transition-colors cursor-pointer rounded-lg
            ${compact ? 'flex-1 flex flex-col m-6' : ''}
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 bg-white'}
            ${files.length > 0 ? 'border-solid' : ''}
          `}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          <div className={`${compact ? 'flex-1 flex flex-col justify-center p-8' : 'p-6'}`}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
              disabled={isUploading}
            />

            {files.length === 0 ? (
              <div className="text-center py-12">
                <Upload className={`${compact ? 'w-24 h-24' : 'w-16 h-16'} mx-auto text-gray-400 mb-6`} />
                <p className={`${compact ? 'text-2xl' : 'text-lg'} font-medium text-gray-700 mb-3`}>
                  Drag & drop your files here
                </p>
                <p className={`${compact ? 'text-base' : 'text-sm'} text-gray-500 mb-6`}>
                  or click to browse
                </p>
                <div className="flex justify-center gap-3 mb-6">
                  <Badge variant="outline" className={compact ? 'text-base px-4 py-1' : ''}>PDF</Badge>
                  <Badge variant="outline" className={compact ? 'text-base px-4 py-1' : ''}>CSV</Badge>
                  <Badge variant="outline" className={compact ? 'text-base px-4 py-1' : ''}>Excel</Badge>
                </div>
                
                {/* ZIP Upload Option */}
                <div className="pt-6 border-t border-gray-200">
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowZipUpload(true); }}
                    className={`inline-flex items-center gap-2 px-4 py-2 ${compact ? 'text-base' : 'text-sm'} text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors`}
                  >
                    <Archive className={compact ? 'w-5 h-5' : 'w-4 h-4'} />
                    Have a ZIP file? Upload all at once
                  </button>
                </div>
              </div>
            ) : (
              <div className={`${compact ? 'p-6' : ''}`} onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`${compact ? 'text-base' : 'text-sm'} font-medium text-gray-700`}>
                    {files.length} file(s) selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFiles([])}
                    disabled={isUploading}
                  >
                    Clear All
                  </Button>
                </div>

                <div className="space-y-3">
                  {files.map((uploadedFile, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                    >
                      {getFileIcon(uploadedFile.file)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {uploadedFile.file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatSize(uploadedFile.file.size)}
                        </p>
                      </div>
                      {!isUploading && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="h-8 w-8 p-0"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {!isUploading && !uploadComplete && (
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Add More Files
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Real-Time Progress Display */}
        {isUploading && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-blue-900">
                    {statusMessage || 'Processing...'}
                  </span>
                  <span className="text-blue-700 font-bold">{uploadProgress}%</span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-blue-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                
                {/* Current File */}
                {currentFile && (
                  <div className="text-sm text-blue-700 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Processing: {currentFile}</span>
                  </div>
                )}
                
                {/* File Progress Summary */}
                <div className="text-xs text-blue-600">
                  {files.length} files queued • Real-time progress streaming
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upload Results */}
        {uploadComplete && stats && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
                <div className="flex-1">
                  <h3 className="font-semibold text-green-900 mb-2">
                    Upload Complete!
                  </h3>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {stats.total_transactions || 0}
                      </div>
                      <div className="text-xs text-gray-500">Transactions</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {stats.total_periods || 0}
                      </div>
                      <div className="text-xs text-gray-500">Periods</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {stats.tax_forms || 0}
                      </div>
                      <div className="text-xs text-gray-500">Tax Forms</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {(stats.total_processing_time_seconds || stats.processing_time || 0).toFixed(2)}s
                      </div>
                      <div className="text-xs text-gray-500">Time</div>
                    </div>
                  </div>

                  {/* File Results */}
                  <div className="space-y-2">
                    {fileResults.map((fr, i) => {
                      // Check if this is a tax form (1065 or K1)
                      const isTaxForm = fr.document_type === '1065' || fr.document_type === 'K1';
                      const hasTaxData = fr.tax_data && Object.keys(fr.tax_data).length > 0;
                      
                      return (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          {fr.success ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="font-medium">{fr.filename}</span>
                          <span className="text-gray-500">
                            {isTaxForm ? (
                              // Tax forms show extracted data info instead of txns/periods
                              hasTaxData ? (
                                `${fr.document_type === '1065' ? 'Form 1065' : 'Schedule K-1'} • Year ${fr.tax_data?.tax_year || 'Unknown'}`
                              ) : (
                                `${fr.document_type === '1065' ? 'Form 1065' : 'Schedule K-1'} • Processing...`
                              )
                            ) : (
                              // Bank statements/CSVs show transactions and periods
                              `${fr.transactions_extracted || fr.transactions || 0} txns • ${fr.periods_count || fr.periods || 0} periods`
                            )}
                          </span>
                          {fr.cache_hit && (
                            <Badge variant="outline" className="text-xs">
                              <Zap className="w-3 h-3 mr-1" />
                              Cached
                            </Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons - only show in non-compact mode */}
        {!compact && (
          <div className="flex items-center justify-between pt-4">
            <Button
              variant="outline"
              onClick={onBack}
              disabled={isUploading}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            <div className="flex gap-3">
              {!uploadComplete && (
                <Button
                  onClick={uploadFiles}
                  disabled={files.length === 0 || isUploading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {statusMessage || `Processing... ${uploadProgress}%`}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Upload & Analyze
                    </>
                  )}
                </Button>
              )}

              {uploadComplete && (
                <Button
                  onClick={handleProceed}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Continue to Review
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Compact mode: inline upload button */}
        {compact && files.length > 0 && !uploadComplete && (
          <div className="pt-4">
            <Button
              onClick={uploadFiles}
              disabled={files.length === 0 || isUploading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {statusMessage || `Processing... ${uploadProgress}%`}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Upload & Analyze ({files.length} files)
                </>
              )}
            </Button>
          </div>
        )}

        {/* Features Banner - hide in compact mode */}
        {!compact && (
          <div className="grid grid-cols-3 gap-4 text-center text-sm text-gray-600 pt-4">
            <div className="flex flex-col items-center gap-2">
              <Zap className="w-6 h-6 text-amber-500" />
              <span>Smart Caching</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <RefreshCw className="w-6 h-6 text-blue-500" />
              <span>Multi-File Parallel</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-500" />
              <span>AI Fallback</span>
            </div>
          </div>
        )}
      </div>
      
      {/* ZIP Upload Modal */}
      {showZipUpload && (
        <ZipBatchUpload
          onUploadComplete={(result) => {
            console.log('ZIP upload complete in UnifiedUploader:', result);
            setShowZipUpload(false);
            
            // Convert ZIP result to UnifiedUploader format and call onComplete
            if (result.canonical_data || result.tax1065Files) {
              // Build the same format that onComplete expects
              const yearlyData: Record<string, any[]> = {};
              
              if (result.canonical_data?.periods) {
                result.canonical_data.periods.forEach((period: any) => {
                  const year = period.year;
                  const month = period.month || 1;
                  const yearKey = `data_${year}`;
                  
                  if (!yearlyData[yearKey]) {
                    yearlyData[yearKey] = [];
                  }
                  
                  yearlyData[yearKey].push({
                    year,
                    month,
                    revenue: period.gross_revenue || 0,
                    cogs: period.cogs || 0,
                    payroll: (period.employee_wages || 0) + (period.officer_compensation || 0),
                    employee_wages: period.employee_wages || 0,
                    officer_compensation: period.officer_compensation || 0,
                    fixed_costs: period.fixed_costs || 0,
                    variable_costs: period.variable_costs || 0,
                    net_profit: period.net_profit || 0,
                    line_items: period.line_items || {},
                    transaction_count: period.transactions?.length || 0,
                  });
                });
                
                // Sort monthly data
                Object.keys(yearlyData).forEach(yearKey => {
                  yearlyData[yearKey].sort((a: any, b: any) => a.month - b.month);
                });
              }
              
              // Call onComplete with the converted data
              onComplete({
                canonical_data: {
                  ...result.canonical_data,
                  // Also include the yearly data format for Step 3
                  financial_data: yearlyData
                },
                file_results: [],
                stats: {
                  files_processed: result.files_processed,
                  total_files: result.total_files,
                  total_transactions: result.canonical_data?.periods?.reduce((sum: number, p: any) => sum + (p.transactions?.length || 0), 0) || 0,
                  total_periods: result.canonical_data?.periods?.length || 0,
                  processing_time_seconds: result.processing_time_seconds
                },
                review_items: [],
                tax1065Files: result.tax1065Files || [],
                fromZipUpload: true,
                unified_upload_data: result
              });
            }
          }}
          onClose={() => setShowZipUpload(false)}
        />
      )}
    </div>
  );
}
